import {
    Component,
    effect,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Signal,
    viewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { PanZoomComponent, PanZoomModel } from 'ngx-panzoom';
import { Subject, Subscription } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ImgDbZoomService } from 'src/app/img-db/services/img-db-zoom.service';
import { ImgAnnotation } from '../../img-db/model/img-annotation';
import { ImgWithSelection } from '../../img-db/model/img-with-selection';
import { PagingResult } from '../../shared/models/pageable';
import { ColorService } from '../../shared/services/color.service';
import { isVisualEvalAnnotation } from '../models/visual-eval-data-set';
import {
    ImageViewerStorageService,
    ShowNameMarker,
} from '../service/image-viewer-storage.service';

@Component({
    selector: 'app-image-viewer',
    templateUrl: './image-viewer.component.html',
    styleUrls: ['./image-viewer.component.scss'],
    providers: [ImageViewerStorageService],
    standalone: false,
})
export class ImageViewerComponent implements OnInit, OnDestroy {
    protected readonly isVisualEvalAnnotation = isVisualEvalAnnotation;
    readonly PAGE_SIZE: number = 8;

    @Input() trainingId: string;
    @Input() selectedImageIndexInAllImages: number;

    currentImage: Signal<ElementRef> = viewChild<ElementRef>('currentImage');
    panZoomParent: Signal<ElementRef> = viewChild<ElementRef>('panzoomParent');
    panZoomComponent: Signal<PanZoomComponent> =
        viewChild<PanZoomComponent>('PanZoom');

    // panZoom configuration object
    panZoomConfig = {
        zoomLevels: ImgDbZoomService.MAX_IMAGE_ZOOM,
        minZoomLevel: ImgDbZoomService.MIN_IMAGE_ZOOM,
        scalePerZoomLevel: 1.2, // 20% zoom per level
        neutralZoomLevel: 10,
        zoomOnMouseWheel: true,
        zoomOnDoubleClick: false,
        invertMouseWheel: true,
        freeMouseWheel: false, // make mouse wheel zoom in 20% steps as defined above
        panOnClickDrag: true,
        zoomToFitZoomLevelFactor: 0.98,
        dragMouseButton: 'left',
    };

    images: ImgWithSelection[] = [];
    selectedImage: ImgWithSelection;
    isLeftArrowVisible: boolean;
    isRightArrowVisible: boolean;
    totalPages: number;
    totalElements: number;
    currentImageNumber: number = 1;
    currentPage: number = 1;
    pagingTimeout: any; //timeout paging debouncing (fetch next page after certain time)
    panDeltaTimeout: any; //timeout panDelta execution

    svgStatus: any;
    annotations: ImgAnnotation[];

    // css variables
    titleBoxHeight: number = 30;

    // used to set and clear delayed alignment after zoom
    zoomAlignmentTimeout: any;
    zoomControl: FormControl<any> = new FormControl();
    targetZoomLevel: number = 0;
    currentZoomLevel: number = 0;

    // check if panning was started and ended
    imagePanned: boolean = false;
    keepInBoundsTranslocationLimit: number = 100000;

    //check if zooming was started and ended
    imageZoomed: boolean = false;

    cursorView: string = 'grab';

    private subscriptions: Subscription[] = [];

    destroy$: Subject<void> = new Subject<void>();

    showNames: ShowNameMarker = {
        showPreTrainName: true,
        showPostTrainName: true,
    };

    showData: boolean = false;
    private resizeObserver: ResizeObserver;

    constructor(
        private colorService: ColorService,
        private imageViewerStorageService: ImageViewerStorageService
    ) {
        this.isLeftArrowVisible = false;
        this.isRightArrowVisible = false;

        effect(() => {
            if (this.panZoomParent()) {
                this.resizeObserver = new ResizeObserver(
                    this.onResize.bind(this)
                );
                this.resizeObserver.observe(this.panZoomParent().nativeElement);
            }
        });

        effect(() => {
            if (this.panZoomComponent()) {
                this.panZoomComponent()['isMobile'] = false; //dirty fix for MouseWheelzoom when Device with touchscreen
                //Do not request model, it's a signal and will trigger effect all the time
                this.targetZoomLevel = this.panZoomConfig.neutralZoomLevel;
                this.currentZoomLevel = this.targetZoomLevel;
                this.zoomControl.setValue(this.currentZoomLevel);
            }
        });
    }

    ngOnInit() {
        if (!this.selectedImageIndexInAllImages) {
            this.selectedImageIndexInAllImages = 0;
        }
        this.currentPage = this.calculatePageNumberFromImageIndexInAllImages(
            this.selectedImageIndexInAllImages
        );
        this.imageViewerStorageService.currentPage = this.currentPage;
        this.imageViewerStorageService.init(this.trainingId);
        this.subscriptions.push(
            this.imageViewerStorageService
                .getImages()
                .pipe(
                    take(1),
                    switchMap((page: PagingResult<ImgWithSelection>) => {
                        this.initializeData(page);
                        return this.imageViewerStorageService.annotations$.pipe(
                            takeUntil(this.destroy$)
                        );
                    }),
                    tap((annotations: ImgAnnotation[]) => {
                        this.annotations = annotations;
                    })
                )
                .subscribe()
        );
        this.imageViewerStorageService.showNameMarkers$.subscribe(
            (showNames: ShowNameMarker) => {
                this.showNames.showPreTrainName = showNames.showPreTrainName;
                this.showNames.showPostTrainName = showNames.showPostTrainName;
            }
        );
    }

    onResize() {
        this.keepImageInBounds();
    }

    private initializeData(page: PagingResult<ImgWithSelection>) {
        this.svgStatus = {
            w: '100%',
            h: '100%',
            viewBox: '0 0 0 0',
        };
        if (page?.values?.length) {
            this.showData = true;
            this.images = page.values;
            this.currentImageNumber = this.selectedImageIndexInAllImages + 1;
            const indexInPage =
                this.selectedImageIndexInAllImages -
                this.PAGE_SIZE * (this.currentPage - 1);
            this.selectImage(indexInPage);
            this.totalElements = page.totalElements;
            this.totalPages = page.totalPages;
            this.checkArrowsAvailability();
        }
    }

    ngOnDestroy(): void {
        // unsubscribe from the subscriptions
        this.resizeObserver?.unobserve(this.panZoomParent()?.nativeElement);
        this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
        this.destroy$.complete();
    }

    // this is called every time the image is dragged or zoomed (for each animation step)
    onModelChanged(model: PanZoomModel) {
        if (model.isPanning) {
            this.imagePanned = true;
            clearTimeout(this.zoomAlignmentTimeout); //abort alignment after zoom
            return; // is panning do nothing
        } else if (this.imagePanned) {
            this.imagePanned = false;
            this.keepImageInBounds();
        } else if (model.zoomLevel !== this.targetZoomLevel) {
            this.targetZoomLevel = this.panZoomComponent().model()?.zoomLevel;
            this.currentZoomLevel = this.targetZoomLevel;
            this.zoomControl.setValue(this.currentZoomLevel);
            // go here if image is currently zooming
            this.actionWhileZoom(model);
        } else if (this.imageZoomed) {
            // go here if image zoom is finished
            this.actionAfterZoom();
        }
    }

    private actionWhileZoom(model: PanZoomModel) {
        this.imageZoomed = true;
        clearTimeout(this.zoomAlignmentTimeout); //abort alignment after zoom

        // set current zoom level to target zoom level of current animation
        if (model.zoomLevel > this.currentZoomLevel) {
            this.targetZoomLevel = Math.ceil(model.zoomLevel);
        } else {
            this.targetZoomLevel = Math.floor(model.zoomLevel);
        }
    }

    private actionAfterZoom() {
        this.imageZoomed = false;
        this.currentZoomLevel = this.targetZoomLevel;
        this.zoomControl.setValue(this.currentZoomLevel);
        // align image after zoom animation is finished with delay
        this.zoomAlignmentTimeout = setTimeout(() => {
            if (!this.imageZoomed) {
                this.keepImageInBounds();
            }
        }, 400);
    }

    private keepImageInBounds() {
        if (
            !this.panZoomComponent() ||
            !this.panZoomParent() ||
            !this.currentImage()
        ) {
            return; //some of the viewChilds not available
        }
        const model = this.panZoomComponent().model();
        if (!model || model.isPanning || this.panDeltaTimeout) {
            return;
        }
        const parentWidth = this.panZoomParent().nativeElement.offsetWidth;
        const parentHeight = this.panZoomParent().nativeElement.offsetHeight;
        const imgWidth =
            this.currentImage().nativeElement.getBoundingClientRect().width;
        const imgHeight =
            this.currentImage().nativeElement.getBoundingClientRect().height;

        // current zoomLevel. This will return 0,1,2,etc, NOT the actual scale like 1, 1.2, 1.44 and so on
        const zoomLevel = model.zoomLevel;

        // actual scale value
        const scaleValue = Math.pow(
            this.panZoomConfig.scalePerZoomLevel,
            zoomLevel
        );

        const newX = this.calculateValue(imgWidth, parentWidth, model.pan.x);
        const newY = this.calculateValue(imgHeight, parentHeight, model.pan.y);

        // set the delta position of the image, i.e. the distance of x and y that the image needs to be panned by
        let dx = ((newX - model.pan.x) * -1) / scaleValue;
        let dy = ((newY - model.pan.y) * -1) / scaleValue;
        this.panDeltaTimeout = setTimeout(() => {
            this.panDeltaWithoutJitter(dx, dy);
            this.panDeltaTimeout = null;
        }, 0);
    }

    private panDeltaWithoutJitter(dx: number, dy: number) {
        // We need to check if there are any differences and only call penDelta when necessary.
        // Calling penDelta all the time can disable zoom.
        // We're comparing the absolute values to 2 and not 0 in order to avoid jittering due to small margins like 0.1, 0.2, etc
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
            this.panZoomComponent().panDelta(
                {
                    x: Math.min(
                        Math.max(dx, -this.keepInBoundsTranslocationLimit),
                        this.keepInBoundsTranslocationLimit
                    ),
                    y: Math.min(
                        Math.max(dy, -this.keepInBoundsTranslocationLimit),
                        this.keepInBoundsTranslocationLimit
                    ),
                },
                0.01
            );
            this.keepInBoundsTranslocationLimit =
                this.keepInBoundsTranslocationLimit * 0.7;
        } else {
            this.keepInBoundsTranslocationLimit = 100000;
        }
    }

    private calculateValue(imgValue: any, parentValue: any, panValue: number) {
        let newValue: number = panValue;
        // if the width of the image is less than or equal to the frame width
        // we need to center the image horizontally
        if (imgValue <= parentValue) {
            newValue = parentValue / 2 - imgValue / 2;
        } else {
            // if the image width is larger than the frame, we need to make sure that edges
            // of the image don't go inside the frame, in other words, the image should always
            // cover the entire width of the frame.
            // x > 0 means the left side of the image inside the frame
            if (!panValue || panValue > 0) {
                newValue = 0; // return the left side of the image on the left edge of the frame, i.e. first valid value
            } else if (panValue + imgValue < parentValue) {
                // check the right side of them image, x + imgWidth < parentWidth means the right side of the image is inside the frame.
                // return it to the position where right side of the image is on the right edge of the frame, i.e. first valid value.
                // x + imgWidth = parentWidth, i.e. x = parentWidth - imgWidth
                newValue = parentValue - imgValue;
            }
        }
        return newValue;
    }

    private changeZoom(zoomLevel: number) {
        if (
            zoomLevel > this.panZoomConfig.zoomLevels ||
            zoomLevel < this.panZoomConfig.minZoomLevel ||
            !this.selectedImage ||
            !this.panZoomComponent()
        ) {
            return;
        }

        this.panZoomComponent().changeZoomLevel(zoomLevel, null);
    }

    onSliderZoomChange() {
        const zoomLevel = this.zoomControl.value;
        this.changeZoom(zoomLevel);
    }

    onZoomIn() {
        const zoomLevel = this.zoomControl.value + 1;
        this.changeZoom(zoomLevel);
    }

    onZoomOut() {
        const zoomLevel = this.zoomControl.value - 1;
        this.changeZoom(zoomLevel);
    }

    selectImage(indexInPage: number) {
        const img = this.images[indexInPage];
        if (this.selectedImage?.id !== img?.id) {
            this.selectedImage = img;
            this.imageViewerStorageService.selectedImageIndex$.next(
                indexInPage + this.PAGE_SIZE * (this.currentPage - 1)
            );
        }
    }

    getColor(categoryName: string): string {
        return this.colorService.getColor(categoryName);
    }

    getColorByAnnotation(
        annotation: ImgAnnotation,
        isAnnotationFillColor: boolean = false
    ) {
        const categoryName: string = this.getImgCategoryName(
            annotation.categoryId
        );

        if (isAnnotationFillColor) {
            return 'transparent';
        }

        return this.getColor(categoryName);
    }

    getImgCategoryName(id: string): string {
        return this.imageViewerStorageService.categories.get(id);
    }

    /**
     * Called from Img-HTML when successfully loaded Image,
     * Load-Event
     */
    onImageLoad(): void {
        this.svgStatus = {
            w: this.currentImage().nativeElement.clientWidth,
            h: this.currentImage().nativeElement.clientHeight,
            viewBox: `0 0 ${this.selectedImage.width} ${this.selectedImage.height}`,
        };
        this.keepImageInBounds();
    }

    getNextPage() {
        if (this.currentPage + 1 <= this.totalPages) {
            this.currentPage++;
            this.imageViewerStorageService.currentPage = this.currentPage;
            this.getNewPage();
        }
    }

    getPrevPage() {
        if (this.currentPage - 1 >= 1) {
            this.currentPage--;
            this.imageViewerStorageService.currentPage = this.currentPage;
            this.getNewPage();
        }
    }

    getNewPage(indexOfImageToBeSelected?: number) {
        // Timeout to prevent unnecessary requests when user clicks repeatedly
        clearTimeout(this.pagingTimeout);
        this.pagingTimeout = setTimeout(() => {
            this.imageViewerStorageService
                .getImages()
                .pipe(take(1))
                .subscribe((page: PagingResult<ImgWithSelection>) => {
                    this.images = page.values;
                    this.checkArrowsAvailability();
                    if (
                        typeof indexOfImageToBeSelected === 'number' &&
                        indexOfImageToBeSelected >= 0
                    ) {
                        this.selectImage(indexOfImageToBeSelected);
                    }
                });
        }, 300);
    }

    private checkArrowsAvailability() {
        this.isRightArrowVisible = this.currentPage < this.totalPages;
        this.isLeftArrowVisible = this.currentPage > 1;
    }

    onNextValue(numberProvidedFromPagingBar: number) {
        if (this.currentImageNumber !== numberProvidedFromPagingBar) {
            this.currentImageNumber = numberProvidedFromPagingBar;

            const indexOfImageToBeSelected: number =
                this.findIndexOfImageToBeSelected(this.currentImageNumber) - 1;

            const newPage: number = this.calculateNextPage(
                numberProvidedFromPagingBar
            );
            if (newPage !== this.currentPage) {
                this.currentPage = newPage;
                this.imageViewerStorageService.currentPage = this.currentPage;
                this.getNewPage(indexOfImageToBeSelected);
            } else {
                this.selectImage(indexOfImageToBeSelected);
            }
        }
    }

    onSelectImage(indexOfSelectedImage: number) {
        this.selectImage(indexOfSelectedImage);
        this.currentImageNumber = this.findImageIndexToProvide();
    }

    private findImageIndexToProvide(): number {
        const idx: number = this.images.findIndex(
            (img: ImgWithSelection) => img.id === this.selectedImage.id
        );

        return this.calculateImageIndexInAllImagesFromImageIndexInPage(idx) + 1;
    }

    private calculateNextPage(val: number): number {
        return Math.ceil(val / this.PAGE_SIZE);
    }

    private calculatePageNumberFromImageIndexInAllImages(
        imageIndexInAllImages: number
    ): number {
        return Math.floor(imageIndexInAllImages / this.PAGE_SIZE) + 1;
    }

    private calculateImageIndexInAllImagesFromImageIndexInPage(
        imageIndexInPage: number
    ): number {
        return imageIndexInPage + this.PAGE_SIZE * (this.currentPage - 1);
    }

    private findIndexOfImageToBeSelected(imageToGet: number): number {
        return imageToGet % this.PAGE_SIZE || this.PAGE_SIZE;
    }

    getLineYCoordinate(
        annotation: ImgAnnotation,
        includeTitleBoxHeight: boolean = true
    ) {
        const titleBoxHeightOrZero = includeTitleBoxHeight
            ? this.titleBoxHeight
            : 0;

        return isVisualEvalAnnotation(annotation)
            ? annotation.bbox.height + annotation.bbox.y + titleBoxHeightOrZero
            : annotation.bbox.y - titleBoxHeightOrZero;
    }
}
